import { useState } from 'react';
import {
  TransformedJobItem,
  JobQuestionResponse,
  JobQuestion,
} from '@worker/types/myJobs.type';
import { useWorkerStore } from '@worker/stores';
import { useWorkerContext } from '@worker/state/workerContext';
import { haversineDistance } from '@utils/GoogleMapsUtils';
import {
  KM_TO_MILES,
  MY_JOBS_DISTANCE_WARNING_MILES,
  MY_JOBS_PROFILE_COMPLETION_THRESHOLD,
} from '@constants/various';
import { useApplyToJobMutation } from './useApplyToJobMutation';
import { useJobQuestions } from './useJobQuestions';

interface ApplyFlowModalsState {
  distance: {
    opened: boolean;
    onClose: () => void;
    onConfirm: () => void;
  };
  profile: {
    opened: boolean;
    onClose: () => void;
    onProceed: () => void;
    isLoading: boolean;
  };
  questions: {
    opened: boolean;
    onClose: () => void;
    onSubmit: (
      responses: JobQuestionResponse[],
      note?: string,
      files?: Map<number, File>
    ) => void;
    questions: JobQuestion[];
    isLoading: boolean;
  };
}

interface UseApplyFlowResult {
  /** Start the apply flow for a job - handles pre-apply checks */
  startApply: (job: TransformedJobItem) => void;
  /** Modal props ready to spread into each modal component */
  modals: ApplyFlowModalsState;
  /** Whether any mutation is pending */
  isApplying: boolean;
}

/**
 * Hook that manages the complete job application flow:
 * 1. Profile completion check (< 90% shows warning)
 * 2. Distance check (> 100 miles shows warning)
 * 3. Questions/Note modal
 * 4. Final apply mutation
 *
 * Extracts all apply flow state and logic from the page component.
 */
export function useApplyFlow(): UseApplyFlowResult {
  // Flow state
  const [applyingToJob, setApplyingToJob] = useState<TransformedJobItem | null>(
    null
  );
  const [showDistanceModal, setShowDistanceModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);

  // External state/hooks
  const { workerProfile } = useWorkerStore();
  const { profileCompletedPercentage } = useWorkerContext();
  const applyMutation = useApplyToJobMutation();

  const { data: jobQuestions = [], isLoading: isLoadingQuestions } =
    useJobQuestions(applyingToJob?.job?.id, showQuestionsModal);

  const resetFlow = () => {
    setApplyingToJob(null);
    setShowDistanceModal(false);
    setShowProfileModal(false);
    setShowQuestionsModal(false);
  };

  const needsDistanceWarning = (job: TransformedJobItem): boolean => {
    // If we already have a calculated distance (from jobAssignment), use it
    if (job.distance !== null && job.distance !== undefined) {
      return job.distance > MY_JOBS_DISTANCE_WARNING_MILES;
    }

    // Otherwise, calculate distance from coordinates (needed for NEW tab)
    const workerLat = workerProfile?.workerProfile?.address?.latitude;
    const workerLng = workerProfile?.workerProfile?.address?.longitude;
    const jobLat = job.job?.address?.latitude;
    const jobLng = job.job?.address?.longitude;

    if (workerLat && workerLng && jobLat && jobLng) {
      // haversineDistance returns kilometers, convert to miles
      const distanceKm = haversineDistance(
        workerLat,
        workerLng,
        jobLat,
        jobLng
      );
      const distanceMiles = distanceKm * KM_TO_MILES;
      return distanceMiles > MY_JOBS_DISTANCE_WARNING_MILES;
    }

    // Can't determine distance, don't show warning
    return false;
  };

  const needsProfileWarning = (): boolean => {
    return (
      profileCompletedPercentage !== undefined &&
      profileCompletedPercentage < MY_JOBS_PROFILE_COMPLETION_THRESHOLD
    );
  };

  const startApply = (job: TransformedJobItem) => {
    setApplyingToJob(job);

    // Flow order: profile first, then distance, then questions
    if (needsProfileWarning()) {
      setShowProfileModal(true);
    } else if (needsDistanceWarning(job)) {
      setShowDistanceModal(true);
    } else {
      setShowQuestionsModal(true);
    }
  };

  const handleProfileProceed = () => {
    setShowProfileModal(false);

    if (applyingToJob && needsDistanceWarning(applyingToJob)) {
      setShowDistanceModal(true);
    } else {
      setShowQuestionsModal(true);
    }
  };

  const handleDistanceConfirm = () => {
    setShowDistanceModal(false);
    setShowQuestionsModal(true);
  };

  const handleQuestionsSubmit = (
    responses: JobQuestionResponse[],
    note?: string,
    files?: Map<number, File>
  ) => {
    if (!applyingToJob?.job?.id) return;

    applyMutation.mutate(
      {
        jobId: applyingToJob.job.id,
        jobName: applyingToJob.job.title,
        note,
        responses,
        files,
      },
      {
        onSettled: () => {
          resetFlow();
        },
      }
    );
  };

  return {
    startApply,
    isApplying: applyMutation.isPending,
    modals: {
      distance: {
        opened: showDistanceModal,
        onClose: resetFlow,
        onConfirm: handleDistanceConfirm,
      },
      profile: {
        opened: showProfileModal,
        onClose: resetFlow,
        onProceed: handleProfileProceed,
        isLoading: false,
      },
      questions: {
        opened: showQuestionsModal,
        onClose: resetFlow,
        onSubmit: handleQuestionsSubmit,
        questions: jobQuestions,
        isLoading: isLoadingQuestions || applyMutation.isPending,
      },
    },
  };
}

export default useApplyFlow;
